'use client';

import React, { useEffect, useMemo, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { toast } from 'react-toastify';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import axios from 'axios';

import getPORequestColumns from '../../utils/getPORequestColumns';
import useUserMap from '../../hooks/useUserMap';
import HistoricalRfqModal from './Modals/HistoricalRfqModal';
import ConfirmDialog from '../UserDialog/ConfirmDialog';
import { useNetSuiteProductionRfq } from '../../config/netsuite';

import {
  ClientSideRowModelModule,
  ClipboardModule,
  ExcelExportModule,
  ColumnMenuModule,
  ContextMenuModule,
  CellSelectionModule,
  TextFilterModule,
  NumberFilterModule,
  DateFilterModule,
  RowStyleModule,
  RowSelectionModule,
  SetFilterModule,
  MultiFilterModule,
  GroupFilterModule,
  PaginationModule,
  ModuleRegistry,
} from 'ag-grid-enterprise';

ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  ClipboardModule,
  ExcelExportModule,
  ColumnMenuModule,
  ContextMenuModule,
  CellSelectionModule,
  TextFilterModule,
  NumberFilterModule,
  DateFilterModule,
  RowStyleModule,
  RowSelectionModule,
  SetFilterModule,
  MultiFilterModule,
  GroupFilterModule,
  PaginationModule,
]);

const AwardedRequests = ({ data, refetch }) => {
  const [rowData, setRowData] = useState([]);
  const userMap = useUserMap();
  const [poDataLoading, setPoDataLoading] = useState(false);
  const [historicalRfqData, setHistoricalRfqData] = useState([]);
  const [historicalModalOpen, setHistoricalModalOpen] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ open: false });

  useEffect(() => {
    setRowData(data || []);
  }, [data]);

  const handleViewPO = async (rfqRow) => {
    setPoDataLoading(true);
    setHistoricalModalOpen(true);

    try {
      const { id: rfqId } = rfqRow;
      const bidSheetItems = await axios.get(`/api/VendorAssignedRfq/RfqDetails/Dataset/${rfqId}`);
      const uniquePoIds = [...new Set(bidSheetItems.data.map((i) => i.netSuitePoId).filter(Boolean))];

      const poDataArray = await Promise.all(
        uniquePoIds.map((poId) =>
          axios
            .get(`/api/NetSuite/PurchaseOrder`, {
              params: { puchaseOrderId: poId, useProduction: useNetSuiteProductionRfq },
            })
            .then((r) => r.data)
        )
      );

      setHistoricalRfqData(poDataArray);
      setHistoricalModalOpen(true);
    } catch (err) {
      console.error('Failed to fetch historical PO data', err);
      toast.error('Could not load submitted POs.');
    } finally {
      setPoDataLoading(false);
    }
  };

  const closeConfirmDialog = () => {
    setConfirmDialog({ open: false });
  };

  const handleDeleteRfq = (rfqRow) => {
    setConfirmDialog({
      open: true,
      message: `Are you sure you want to archive "${rfqRow.name}"?`,
      onConfirm: async () => {
        try {
          await axios.put(`/api/RfqHeader/rfq-isdelete`, {
            id: rfqRow.id,
            isDeleted: true,
          });

          toast.success('Request archived successfully');
          await refetch();
        } catch (err) {
          console.error(err);
          if (err.response?.status === 403) {
            toast.error('Elevated permissions required for this action.');
          } else {
            toast.error('Archive failed');
          }
        } finally {
          closeConfirmDialog();
        }
      },
      onCancel: closeConfirmDialog,
    });
  };

  const actionsRenderer = (params) => (
    <Box display="flex" alignItems="center" justifyContent="center" height="100%" paddingY={0.5} gap={1}>
      <Tooltip title="View NetSuite PO">
        <IconButton size="small" color="primary" onClick={() => handleViewPO(params.data)}>
          <VisibilityIcon fontSize="medium" />
        </IconButton>
      </Tooltip>

      <Tooltip title="Archive Request">
        <IconButton size="small" color="error" onClick={() => handleDeleteRfq(params.data)}>
          <DeleteIcon fontSize="medium" />
        </IconButton>
      </Tooltip>
    </Box>
  );

  const columnDefs = useMemo(() => getPORequestColumns(userMap, actionsRenderer), [userMap]);

  const defaultColDef = {
    sortable: true,
    filter: true,
    resizable: true,
    cellStyle: {
      textAlign: 'left',
    },
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '80vh',
        padding: 2,
      }}
    >
      <Box sx={{ flexGrow: 1, minHeight: 0 }}>
        <div style={{ height: '100%', width: '100%' }}>
          <AgGridReact columnDefs={columnDefs} defaultColDef={defaultColDef} rowData={rowData} pagination={true} />
        </div>
      </Box>

      {historicalModalOpen && (
        <HistoricalRfqModal
          open={historicalModalOpen}
          onClose={() => setHistoricalModalOpen(false)}
          poData={historicalRfqData}
          loading={poDataLoading}
        />
      )}

      <ConfirmDialog
        open={confirmDialog.open}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onCancel={confirmDialog.onCancel}
        onClose={closeConfirmDialog}
      />
    </Box>
  );
};

export default AwardedRequests;
